import { useEffect, useRef, useState } from "react";
import { callFinnhub } from "../utils/finnhubClient";
import { UI } from "../constants/tokens";
import { companyName } from "../utils/companyName";


// Finnhub's /search spans every exchange it knows, but the free tier only
// prices US listings — /quote on a foreign one answers 403, so a symbol we
// show but can't price becomes a holding that never gets a value. The list is
// therefore narrowed to what US exchanges actually carry.
//
// Shape verified against /stock/symbol?exchange=US (30,982 rows): plain
// symbols run 1..6 chars, and the only dotted suffixes holding real class
// shares are .A .B .C .V — BRK.B, BF.B, CRD.A and MKC.V all quote fine.
// .G .H .L .W .X .Y .Z exist solely as ATEST/PTEST exchange test tickers,
// and allowing .L would pull in London. Every other dot is a foreign MIC.
const US_SYMBOL = /^[A-Z][A-Z0-9]{0,5}(\.[ABCV])?$/;

// Not a whitelist of instrument types: Finnhub uses ~28 of them on US
// exchanges alone and leaves 755 rows blank, so an unknown or empty type
// passes. Only the classes a holding can't meaningfully be opened in drop out.
// This is what used to hide TSM — it is an ADR, and the old whitelist took
// Common Stock and ETP only.
const BLOCKED_TYPES = new Set(["bond", "equity wrt", "warrant", "right", "unit", "ltd part"]);

async function searchSymbols(query) {
  if (query.length < 2) return [];
  try {
    // Through the proxy — see netlify/functions/finnhub.js.
    const payload = await callFinnhub("search", { q: query });
    if (!payload) return [];
    const data = payload.result || {};

    const seen = new Set();
    const hits = (data.result || []).filter(r => {
      const symbol = r.symbol || "";
      if (BLOCKED_TYPES.has((r.type || "").trim().toLowerCase())) return false;
      if (!US_SYMBOL.test(symbol)) return false;
      // Finnhub repeats a symbol across listing types — BABA comes back both
      // as ADR and as Common Stock. Keeping the first also keeps the row keys
      // below unique.
      if (seen.has(symbol)) return false;
      seen.add(symbol);
      return true;
    });

    // Finnhub's own ranking buries the exact ticker: searching TSM returns it
    // behind Huntsman and Sportsman's Warehouse. An exact hit is always what
    // was meant, so it leads; Array.sort is stable, so the rest keeps
    // Finnhub's order.
    const typed = query.toUpperCase();
    const isExact = (r) => (r.symbol === typed ? 1 : 0);
    hits.sort((a, b) => isExact(b) - isExact(a));

    return hits.slice(0, 8);
  } catch {
    return [];
  }
}

export default function SymbolAutocomplete({ value, onChange, inputStyle, label, placeholder }) {
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    function onMouseDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9.]/g, "");
    onChange(v);
    setActiveIdx(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.length >= 2) {
      debounceRef.current = setTimeout(async () => {
        const res = await searchSymbols(v);
        setResults(res);
        setOpen(res.length > 0);
      }, 300);
    } else {
      setResults([]);
      setOpen(false);
    }
  };

  const select = (symbol) => {
    onChange(symbol);
    setOpen(false);
    setResults([]);
    setActiveIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); select(results[activeIdx].symbol); }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {label && (
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: UI.text.secondary, marginBottom: 6 }}>
          {label}
        </label>
      )}
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => results.length > 0 && setOpen(true)}
        // Closes on blur — never selects. The 120ms defer lets a pending
        // onMouseDown selection on a result row (which fires before blur)
        // run first, so an explicit click still wins; blur itself never
        // picks an item. Covers Tab-away too, which no mousedown listener sees.
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder}
        style={inputStyle}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (
        <div dir="ltr" style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0,
          right: 0,
          background: UI.surface.overlay,
          border: `1px solid ${UI.line.hairline}`,
          borderRadius: 6,
          zIndex: 200,
          overflow: "hidden",
          boxShadow: UI.shadow.pop,
        }}>
          {results.map((r, i) => (
            <div
              key={r.symbol}
              onMouseDown={() => select(r.symbol)}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseLeave={() => setActiveIdx(-1)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                cursor: "pointer",
                background: i === activeIdx ? UI.fill.subtle : "transparent",
                borderBottom: i < results.length - 1 ? `1px solid ${UI.line.hairline}` : "none",
                transition: "background .1s",
              }}
            >
              <span style={{ fontWeight: 800, fontSize: 14, color: UI.text.primary, minWidth: 58, flexShrink: 0 }}>{r.symbol}</span>
              <span style={{ fontSize: 12, color: UI.text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {companyName(r.description)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
